import { useMemo } from 'react';
import { Grid, View } from '@adobe/react-spectrum';
import { MobileLayout } from '../components/MobileLayout';
import { ServiceActivitiesLayoutProps } from '../types';

/**
 * ServiceActivitiesLayout Component
 *
 * Handles layout routing logic for ServiceActivities component.
 * Routes between mobile and desktop layouts based on device detection,
 * and manages view mode switching (Full vs Detail).
 *
 * This component follows React best practices:
 * - Uses memoization for expensive calculations
 * - Extracts complex conditionals to helper functions
 * - Memoizes style objects to prevent re-creation on every render
 * - Uses proper TypeScript types throughout
 *
 * @param props - Component props including device info, view state, and render functions
 */
export function ServiceActivitiesLayout(props: ServiceActivitiesLayoutProps) {
  const {
    pageView,
    showMobileDetailView,
    deviceInfo,
    mobileLayout,
    filterSlot,
    headerSlot,
    cardSlot,
    tabletFilterSlot,
    detailSlot,
    tableSlot,
  } = props;

  // Memoize device type calculations to prevent recalculation on every render
  const isTabletOrIPad = useMemo(
    () => deviceInfo.isTablet || deviceInfo.isIPad,
    [deviceInfo.isTablet, deviceInfo.isIPad],
  );

  const isTabletLandscape = useMemo(
    () => isTabletOrIPad && deviceInfo.orientation === 'landscape',
    [isTabletOrIPad, deviceInfo.orientation],
  );

  // Calculate desktop layout requirement
  const shouldUseDesktopLayout = useMemo(
    () =>
      !deviceInfo.shouldUseMobileLayout() ||
      (isTabletOrIPad && deviceInfo.orientation === 'landscape'),
    [deviceInfo, isTabletOrIPad],
  );

  // Memoize grid areas configuration
  const gridAreas = useMemo(() => {
    if (isTabletOrIPad) {
      return pageView === 'Full'
        ? ['header header', 'filter filter', 'card detail']
        : ['header header', 'filter filter', 'detail detail'];
    }
    return pageView === 'Full'
      ? ['filter header header', 'filter card detail']
      : ['filter header header', 'filter detail detail'];
  }, [isTabletOrIPad, pageView]);

  // Memoize column configuration
  const columns = useMemo(
    () => ({
      base: isTabletLandscape ? ['35%', '65%'] : ['275px', '28%', 'auto'],
      L: isTabletLandscape ? ['35%', '65%'] : ['275px', '28%', 'auto'],
    }),
    [isTabletLandscape],
  );

  // Memoize row configuration
  const rows = useMemo(
    () => (isTabletOrIPad ? ['auto', 'auto', '1fr'] : ['auto', '1fr']),
    [isTabletOrIPad],
  );

  // Memoize style objects to prevent re-creation on every render
  const gridStyle = useMemo(
    () => ({
      backgroundColor: '#f5f6f9',
      padding: '0px',
      margin: '0px',
      overflow: 'hidden',
    }),
    [],
  );

  const headerStyle = useMemo(
    () => (pageView === 'Full' ? {} : { height: '100%' }),
    [pageView],
  );

  const tabletFilterStyle = useMemo(
    () => ({
      height: '60px',
      backgroundColor: 'white',
      borderBottom: '1px solid #e0e0e0',
    }),
    [],
  );

  const cardListStyle = useMemo(
    () => ({
      backgroundColor: '#f5f6f9',
    }),
    [],
  );

  // Render mobile layout
  if (!shouldUseDesktopLayout && mobileLayout) {
    return mobileLayout;
  }

  // Render desktop/tablet layout
  return (
    <Grid
      areas={gridAreas}
      columns={columns}
      rows={rows}
      height="100%"
      width="100%"
      UNSAFE_style={gridStyle}
    >
      {/* Filter Area - Hidden on tablet landscape, shown for desktop */}
      {!isTabletOrIPad && filterSlot && (
        <View gridArea="filter">{filterSlot}</View>
      )}

      {/* Header Area */}
      <View width={'100%'} gridArea="header" UNSAFE_style={headerStyle}>
        {headerSlot}
      </View>

      {/* Tablet/iPad Filter Area - Shown for tablets/iPads under header */}
      {isTabletOrIPad && tabletFilterSlot && (
        <View gridArea="filter" UNSAFE_style={tabletFilterStyle}>
          {tabletFilterSlot}
        </View>
      )}

      {/* Full View Mode - Shows card list and detail side by side */}
      {pageView === 'Full' ? (
        <>
          {/* Card List Area */}
          {cardSlot && (
            <View gridArea="card" UNSAFE_style={cardListStyle}>
              {cardSlot}
            </View>
          )}

          {/* Detail Area */}
          <View gridArea="detail">{detailSlot}</View>
        </>
      ) : (
        /* Detail View Mode - Shows table view only */
        <View gridArea="detail">{tableSlot || detailSlot}</View>
      )}
    </Grid>
  );
}

